/*
 * UNIK wishlist, local storage (F4-06).
 *
 * Same keys and format as the Shella theme, so favourites saved on the live store are
 * read without conversion:
 *  - `wishlist-guest`, or `wishlist-customer-<id>` when signed in;
 *  - a JSON list of single-key objects: [{"<variantId>": "<handle>"}].
 * Reading removes duplicates and invalid entries. When a customer is signed in, the guest
 * list is merged into the customer's list and the guest key removed (parity).
 *
 * window.UnikWishlist: items(), has(id), add(id, handle), remove(id), clear(), count(),
 * and setRemote(adapter) for account sync (F4-07). A failing adapter never blocks the
 * local list. Changes fire `unik:wishlist-change` on document (also across tabs).
 *
 * Elements: <unik-wishlist-button data-variant-id data-handle> (cards and product page)
 * and <unik-wishlist-count> (header). Config: #UnikWishlistConfig[data-customer-id].
 */
(() => {
  if (window.UnikWishlist) return;

  // sections/unik-wishlist.liquid reads the same keys inline to reserve the page's placeholders.
  const GUEST_KEY = 'wishlist-guest';
  const EVENT = 'unik:wishlist-change';
  let remote = null;

  const customerId = () => document.getElementById('UnikWishlistConfig')?.dataset.customerId || '';
  const storageKey = () => (customerId() ? `wishlist-customer-${customerId()}` : GUEST_KEY);

  function readKey(key) {
    try {
      const value = JSON.parse(window.localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value : [];
    } catch {
      return [];
    }
  }

  function writeKey(key, list) {
    try {
      window.localStorage.setItem(key, JSON.stringify(list));
      return true;
    } catch {
      return false;
    }
  }

  /** Shella format, without duplicates (first wins) or invalid entries. */
  function normalize(list) {
    const seen = new Set();
    const result = [];
    for (const entry of list) {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
      for (const [id, handle] of Object.entries(entry)) {
        const key = String(id);
        if (!/^\d+$/.test(key) || seen.has(key) || typeof handle !== 'string' || !handle) continue;
        seen.add(key);
        result.push({ [key]: handle });
      }
    }
    return result;
  }

  function stored() {
    return normalize(readKey(storageKey()));
  }

  function emit() {
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { count: stored().length } }));
  }

  function save(list) {
    writeKey(storageKey(), normalize(list));
    emit();
  }

  function mergeGuestList() {
    if (!customerId()) return;
    const guest = readKey(GUEST_KEY);
    if (!guest.length) return;
    if (writeKey(storageKey(), normalize([...readKey(storageKey()), ...guest]))) {
      try {
        window.localStorage.removeItem(GUEST_KEY);
      } catch {}
    }
  }

  function callRemote(method, ...args) {
    if (!remote?.[method]) return;
    Promise.resolve()
      .then(() => remote[method](...args))
      .catch(() => {});
  }

  const api = {
    items() {
      return stored().map((entry) => {
        const [variantId, handle] = Object.entries(entry)[0];
        return { variantId, handle };
      });
    },
    count() {
      return stored().length;
    },
    has(variantId) {
      return stored().some((entry) => Object.prototype.hasOwnProperty.call(entry, String(variantId)));
    },
    add(variantId, handle) {
      if (!variantId || !handle || api.has(variantId)) return;
      save([...stored(), { [String(variantId)]: handle }]);
      callRemote('add', String(variantId), handle);
    },
    remove(variantId) {
      save(stored().filter((entry) => !Object.prototype.hasOwnProperty.call(entry, String(variantId))));
      callRemote('remove', String(variantId));
    },
    clear() {
      save([]);
      callRemote('replace', []);
    },
    /** Account sync adapter (F4-07): { load, merge, add, remove, replace }. */
    setRemote(adapter) {
      remote = adapter || null;
    },
    EVENT,
  };

  window.UnikWishlist = api;
  mergeGuestList();
  window.addEventListener('storage', (event) => {
    if (event.key === storageKey() || event.key === GUEST_KEY) emit();
  });

  class UnikWishlistButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button || this.handlers) return;
      this.handlers = {
        click: (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.toggle();
        },
        change: () => this.render(),
      };
      this.button.addEventListener('click', this.handlers.click);
      document.addEventListener(EVENT, this.handlers.change);
      // On the product page the saved variant follows the selected variant.
      const productInfo = this.closest('product-info');
      if (productInfo && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data?.sectionId !== productInfo.dataset.section || !data.variant) return;
          this.dataset.variantId = String(data.variant.id);
          this.render();
        });
      }
      this.render();
    }

    disconnectedCallback() {
      if (!this.handlers) return;
      this.button.removeEventListener('click', this.handlers.click);
      document.removeEventListener(EVENT, this.handlers.change);
      this.unsubscribe?.();
      this.handlers = null;
    }

    toggle() {
      const { variantId, handle } = this.dataset;
      if (api.has(variantId)) api.remove(variantId);
      else api.add(variantId, handle);
    }

    render() {
      const added = api.has(this.dataset.variantId);
      this.button.setAttribute('aria-pressed', String(added));
      const label = added ? this.dataset.labelRemove : this.dataset.labelAdd;
      if (label) this.button.setAttribute('aria-label', label);
      this.toggleAttribute('data-added', added);
      const text = this.querySelector('[data-unik-wishlist-text]');
      if (text && label) text.textContent = label;
    }
  }

  class UnikWishlistCount extends HTMLElement {
    connectedCallback() {
      this.onChange = () => this.render();
      document.addEventListener(EVENT, this.onChange);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT, this.onChange);
    }

    render() {
      const count = api.count();
      this.textContent = count > 0 && count < 100 ? String(count) : count >= 100 ? '99+' : '';
      this.hidden = count === 0;
      const link = this.closest('a');
      const label = link?.dataset.labelCount;
      if (link && label) link.setAttribute('aria-label', label.replace('__count__', String(count)));
    }
  }

  customElements.define('unik-wishlist-button', UnikWishlistButton);
  customElements.define('unik-wishlist-count', UnikWishlistCount);
})();
